<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useField } from 'vee-validate'
import { AlertCircle } from 'lucide-vue-next'
import type { FormField } from '../../types/formTypeStructure'
import FormFieldWrapper from '../../FormFieldWrapper.vue'
import { getLocalizedText } from '../../utils/formHelpers'

interface Props {
  field: FormField & { fieldType: 'OTP' }
  name?: string
  currentLang?: string
  defaultValue?: string
  className?: string
  onOTPComplete?: (otpCode: string) => void
}

const props = defineProps<Props>()
const fieldName = props.name ?? props.field.fieldName.toString()
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))
const otpLength = computed(() => props.field.otpFormFieldExecOptions?.otpLength ?? 6)

const {
  value,
  errorMessage,
  setValue,
  setTouched
} = useField<string>(fieldName, undefined, {
  initialValue: props.defaultValue ?? ''
})

const otpValues = ref<string[]>(Array(otpLength.value).fill(''))
const inputRefs = ref<(HTMLInputElement | null)[]>([])

// Synchroniser avec la valeur du formulaire
watch(() => value.value, (newValue) => {
  if (newValue && typeof newValue === 'string' && newValue.length === otpLength.value) {
    const values = newValue.split('').slice(0, otpLength.value)
    otpValues.value = values
  }
}, { immediate: true })

watch(() => props.defaultValue, (newValue) => {
  if (newValue && typeof newValue === 'string' && newValue.length === otpLength.value) {
    const values = newValue.split('').slice(0, otpLength.value)
    otpValues.value = values
  }
}, { immediate: true })

// Mettre à jour le formulaire quand les valeurs changent
watch(otpValues, (newValues) => {
  const otpString = newValues.join('')
  if (otpString.length === otpLength.value) {
    setValue(otpString, true)
    setTouched(true)
    // Déclencher le callback si fourni
    if (props.onOTPComplete) {
      setTimeout(() => {
        props.onOTPComplete?.(otpString)
      }, 100)
    }
  } else {
    setValue(otpString, false)
    setTouched(true)
  }
}, { deep: true })

const handleOTPChange = (index: number, value: string) => {
  // Ne permettre que les chiffres
  const numericValue = value.replace(/\D/g, '')
  
  if (numericValue.length > 1) {
    // Si plusieurs caractères, prendre le dernier
    const lastChar = numericValue.slice(-1)
    updateOTPValue(index, lastChar)
  } else if (numericValue.length === 1) {
    updateOTPValue(index, numericValue)
  } else {
    updateOTPValue(index, '')
  }
}

const updateOTPValue = (index: number, value: string) => {
  const newValues = [...otpValues.value]
  newValues[index] = value
  otpValues.value = newValues

  // Auto-avancement vers la case suivante
  if (value && index < otpLength.value - 1) {
    setTimeout(() => {
      inputRefs.value[index + 1]?.focus()
    }, 10)
  }
}

const handleKeyDown = (index: number, e: KeyboardEvent) => {
  // Navigation avec les flèches
  if (e.key === 'ArrowLeft' && index > 0) {
    e.preventDefault()
    inputRefs.value[index - 1]?.focus()
  } else if (e.key === 'ArrowRight' && index < otpLength.value - 1) {
    e.preventDefault()
    inputRefs.value[index + 1]?.focus()
  }
  
  // Backspace : effacer et revenir en arrière
  if (e.key === 'Backspace' && !otpValues.value[index] && index > 0) {
    e.preventDefault()
    inputRefs.value[index - 1]?.focus()
  }
  
  // Delete : effacer la case courante
  if (e.key === 'Delete') {
    updateOTPValue(index, '')
  }
}

const handlePaste = (e: ClipboardEvent) => {
  e.preventDefault()
  const pastedData = e.clipboardData?.getData('text').replace(/\D/g, '') ?? ''
  const values = pastedData.slice(0, otpLength.value).split('')
  
  const newValues = [...otpValues.value]
  values.forEach((char, i) => {
    if (i < otpLength.value) {
      newValues[i] = char
    }
  })
  
  otpValues.value = newValues
  
  // Focus sur la dernière case remplie ou la première vide
  const lastFilledIndex = Math.min(values.length - 1, otpLength.value - 1)
  setTimeout(() => {
    inputRefs.value[lastFilledIndex]?.focus()
  }, 10)
}

onMounted(() => {
  // Auto-focus sur la première case si vide
  if (!otpValues.value.some(v => v)) {
    inputRefs.value[0]?.focus()
  }
})
</script>

<template>
  <div class="otp-field-container">
    <!-- Label dupliqué au-dessus -->
    <div class="otp-field-label-container">
      <span class="otp-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <div :class="['flex gap-2 justify-center', props.className]">
        <input
          v-for="(value, index) in otpValues"
          :key="index"
          :ref="(el) => { inputRefs[index] = el as HTMLInputElement }"
          type="text"
          inputmode="numeric"
          maxlength="1"
          :value="value"
          @input="(e) => handleOTPChange(index, (e.target as HTMLInputElement).value)"
          @keydown="(e) => handleKeyDown(index, e as KeyboardEvent)"
          @paste="handlePaste"
          :autofocus="index === 0 && !otpValues.some(v => v)"
          class="w-12 h-12 text-center text-2xl font-bold border-2 border-[#04406E] rounded-lg focus:border-[#033a5a] focus:ring-2 focus:ring-[#e6f0f5] outline-none transition-all"
          style="color: #04406E;"
        />
      </div>
      <p v-if="errorMessage" class="text-sm text-red-500 mt-2 text-center">{{ errorMessage }}</p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.otp-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.otp-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.otp-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>

